import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { ConfirmPopover } from '../ConfirmPopover'
import type { Category } from './vaultData'

/** Rename / delete a custom vault category (OF vault/lists). Reorder is drag on the list. */
export function CategoryActions({
  category,
  anchor,
  onRename,
  onDelete,
  onClose,
}: {
  category: Category
  /** Viewport position next to the right-clicked row (fixed; avoids list overflow clip). */
  anchor: { top: number; right: number }
  onRename: (name: string) => Promise<void>
  onDelete: (clearMedia: boolean) => Promise<void>
  onClose: () => void
}) {
  const { t } = useTranslation('library')
  const [renaming, setRenaming] = useState(false)
  const [name, setName] = useState(category.name)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const run = async (work: () => Promise<void>) => {
    setBusy(true)
    setError('')
    try {
      await work()
      onClose()
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : t('error'))
      setBusy(false)
    }
  }

  const pin = { position: 'fixed' as const, top: anchor.top, right: anchor.right, left: 'auto' as const }

  return (
    <>
      <div className="lib-backdrop" onClick={onClose} />
      <div className="lib-pop lib-menu lib-menu--row" role="menu" aria-label={category.name} style={pin}>
        {renaming ? (
          <div className="lib-menu-edit">
            <input
              className="ofp-input"
              value={name}
              maxLength={80}
              autoFocus
              aria-label={t('vault.renameCategory')}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && name.trim() && void run(() => onRename(name.trim()))}
            />
            <button type="button" className="lib-btn lib-btn--primary" disabled={busy || !name.trim()} onClick={() => void run(() => onRename(name.trim()))}>
              {t('vault.rename')}
            </button>
          </div>
        ) : (
          <>
            <button type="button" role="menuitem" className="lib-menu-item" disabled={busy || !category.canUpdate} onClick={() => setRenaming(true)}>
              <FeatherIcon name="edit-2" size={16} />
              <span className="lib-menu-label">{t('vault.rename')}</span>
            </button>
            <button
              type="button"
              role="menuitem"
              className="lib-menu-item"
              disabled={busy || !category.canDelete}
              onClick={() => setConfirmDelete(true)}
            >
              <FeatherIcon name="trash-2" size={16} />
              <span className="lib-menu-label">{t('vault.deleteCategory')}</span>
            </button>
          </>
        )}
        {error && <p className="ofp-error">{error}</p>}
      </div>
      {confirmDelete && (
        <div className="lib-row-menu-pin" style={pin}>
          <ConfirmPopover
            title={t('vault.deleteCategoryTitle', { name: category.name })}
            body={t('vault.deleteCategoryBody')}
            confirmLabel={t('vault.deleteCategory')}
            onConfirm={() => void run(() => onDelete(false))}
            onCancel={() => setConfirmDelete(false)}
          />
        </div>
      )}
    </>
  )
}
